/**
 * Sorting and per-column filtering for the deals table.
 *
 * Kept out of the components so the values a filter offers are derived from
 * the same functions that render the cells: the Invoice and Sync columns show
 * computed labels ("Past", "Linked (invoice)") rather than raw API fields, and
 * a filter built from the raw fields would offer options that match nothing.
 */

import type { DealMapping } from '../types/api';
import { describeInvoiceStatus } from './invoiceStatus';
import { describeSyncStatus } from './syncStatus';

/** Sentinel for "no filter on this column". Radix Select forbids "". */
export const ALL_FILTER = 'all';

/** Sentinel for rows the column has no value for — the em-dash cells. */
export const NO_VALUE_FILTER = '__none__';

export type ColumnFilterKey = 'quote' | 'invoice' | 'sync';

export type ColumnFilters = Record<ColumnFilterKey, string>;

export const NO_COLUMN_FILTERS: ColumnFilters = {
  quote: ALL_FILTER,
  invoice: ALL_FILTER,
  sync: ALL_FILTER,
};

export type SortKey = 'amount' | 'last_modified';

export type SortDirection = 'asc' | 'desc';

export interface DealSort {
  key: SortKey;
  direction: SortDirection;
}

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * The label each filterable column shows for a deal, or null when the cell is
 * empty. These must stay in step with what DealsTable renders.
 *
 * Sync has no null case: describeSyncStatus falls back to "Not synced" for
 * anything it doesn't recognise, so every deal lands in some bucket.
 */
const COLUMN_LABEL: Record<ColumnFilterKey, (deal: DealMapping) => string | null> = {
  quote: (deal) => deal.quote_status?.trim() || null,
  invoice: (deal) => describeInvoiceStatus(deal)?.label ?? null,
  sync: (deal) => describeSyncStatus(deal.sync_status).label,
};

/** The filter value a deal carries for a column, using the empty sentinel. */
export function columnFilterValue(key: ColumnFilterKey, deal: DealMapping): string {
  return COLUMN_LABEL[key](deal) ?? NO_VALUE_FILTER;
}

/**
 * The options a column's select should offer, derived from the deals in hand.
 *
 * Derived rather than hardcoded because two of the three columns carry
 * open-ended values: quote_status is a HubSpot enumeration the tenant
 * configures, and an invoice status Stripe adds later renders as itself. A
 * fixed list would silently omit them.
 *
 * `selected` is kept even when no deal currently carries it, so a filter that
 * a search has narrowed out of existence still shows in its trigger instead of
 * leaving the control blank.
 */
export function buildFilterOptions(
  deals: readonly DealMapping[],
  key: ColumnFilterKey,
  selected: string = ALL_FILTER,
): FilterOption[] {
  // Keyed case-insensitively — HubSpot returns "Signed" where a hand-entered
  // value might say "signed", and they are one option — but the first spelling
  // seen is what gets shown.
  const seen = new Map<string, string>();
  let hasEmpty = false;
  for (const deal of deals) {
    const label = COLUMN_LABEL[key](deal);
    if (label === null) {
      hasEmpty = true;
      continue;
    }
    const dedupeKey = label.toLowerCase();
    if (!seen.has(dedupeKey)) seen.set(dedupeKey, label);
  }
  if (selected !== ALL_FILTER && selected !== NO_VALUE_FILTER) {
    if (!seen.has(selected.toLowerCase())) seen.set(selected.toLowerCase(), selected);
  }
  if (selected === NO_VALUE_FILTER) hasEmpty = true;

  const options: FilterOption[] = [{ value: ALL_FILTER, label: 'All' }];
  for (const label of [...seen.values()].sort((a, b) => a.localeCompare(b))) {
    options.push({ value: label, label });
  }
  // Last, and named rather than shown as the bare em dash the cell uses: on
  // its own in a menu "—" reads as a disabled item.
  if (hasEmpty) options.push({ value: NO_VALUE_FILTER, label: 'None' });
  return options;
}

/** All three columns' options in one pass-friendly shape. */
export function buildAllFilterOptions(
  deals: readonly DealMapping[],
  filters: ColumnFilters,
): Record<ColumnFilterKey, FilterOption[]> {
  return {
    quote: buildFilterOptions(deals, 'quote', filters.quote),
    invoice: buildFilterOptions(deals, 'invoice', filters.invoice),
    sync: buildFilterOptions(deals, 'sync', filters.sync),
  };
}

export function matchesColumnFilters(deal: DealMapping, filters: ColumnFilters): boolean {
  return (Object.keys(NO_COLUMN_FILTERS) as ColumnFilterKey[]).every((key) => {
    const wanted = filters[key];
    if (wanted === ALL_FILTER) return true;
    const actual = columnFilterValue(key, deal);
    if (wanted === NO_VALUE_FILTER) return actual === NO_VALUE_FILTER;
    return actual.toLowerCase() === wanted.toLowerCase();
  });
}

export function hasActiveColumnFilters(filters: ColumnFilters): boolean {
  return (Object.keys(NO_COLUMN_FILTERS) as ColumnFilterKey[]).some(
    (key) => filters[key] !== ALL_FILTER,
  );
}

/**
 * The value a sort compares, or null when the deal has none.
 *
 * Amount is compared as the raw number: deals can be in different currencies
 * and there are no rates here to convert with, so a mixed-currency portal
 * sorts by figure rather than by true value. Sorting the numbers people can
 * see beats refusing to sort.
 */
function sortValue(deal: DealMapping, key: SortKey): number | null {
  if (key === 'amount') {
    return typeof deal.amount === 'number' && Number.isFinite(deal.amount)
      ? deal.amount
      : null;
  }
  if (!deal.last_modified_at) return null;
  const at = new Date(deal.last_modified_at).getTime();
  return Number.isNaN(at) ? null : at;
}

/**
 * Deals in sort order. Returns a new array; the input is left alone.
 *
 * Deals with no value sort last in both directions — an unpriced deal is not
 * the cheapest one, and floating them to the top of an ascending sort would
 * bury the smallest real amounts. Ties keep their incoming order (the API's
 * ordering), since Array#sort is stable.
 */
export function sortDeals(
  deals: readonly DealMapping[],
  sort: DealSort | null,
): DealMapping[] {
  if (!sort) return [...deals];
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...deals].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av === bv) return 0;
    return av < bv ? -factor : factor;
  });
}

/**
 * The next sort state when a column header is clicked.
 *
 * Descending first — the largest amount and the most recent change are what
 * people are looking for — then ascending, then back to the API's own order,
 * so a sort can be undone with the control that applied it.
 */
export function nextSort(current: DealSort | null, key: SortKey): DealSort | null {
  if (!current || current.key !== key) return { key, direction: 'desc' };
  if (current.direction === 'desc') return { key, direction: 'asc' };
  return null;
}
